import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Col,
    Descriptions,
    Form,
    Input,
    InputNumber,
    Modal,
    Row,
    Select,
    Space,
    Statistic,
    Table,
    message,
} from 'antd';
import { CloudSyncOutlined, EditOutlined, ReloadOutlined } from '@ant-design/icons';
import PageHeader from '../../components/business/PageHeader';
import StatusTag, { statusLabel } from '../../components/business/StatusTag';
import { InboundMailMessage, MailAccount, MailboxMonitor } from '../../models/mail';
import { PageResult } from '../../models/v2';
import { v2Api } from '../../services/v2';
import { asV2Error, formatDateTime } from '../../utils/v2';
import { capabilityMessage, localizedMailError, mailFieldLabel } from './mailUi';
import { MAIL_PAGE_SIZE_OPTIONS, readMailPageSize, rememberMailPageSize } from './mailPagination';

const emptyMessages: PageResult<InboundMailMessage> = {
    items: [],
    pageNo: 1,
    pageSize: readMailPageSize('inbound-messages', 20),
    total: 0,
};

const InboundMonitorPage = () => {
    const [accountForm] = Form.useForm();
    const [configForm] = Form.useForm();
    const [reconcileForm] = Form.useForm();
    const [mailAccountId, setMailAccountId] = useState<number | null>(null);
    const [monitor, setMonitor] = useState<MailboxMonitor | null>(null);
    const [monitorError, setMonitorError] = useState<string | null>(null);
    const [messages, setMessages] = useState(emptyMessages);
    const [filters, setFilters] = useState<any>({
        pageNo: 1,
        pageSize: readMailPageSize('inbound-messages', 20),
        matchStatus: 'MATCHED',
    });
    const [messageError, setMessageError] = useState<string | null>(null);
    const [editing, setEditing] = useState(false);
    const [reconciling, setReconciling] = useState<InboundMailMessage | null>(null);
    const [loadingMonitor, setLoadingMonitor] = useState(false);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [saving, setSaving] = useState(false);
    const [mailAccounts, setMailAccounts] = useState<MailAccount[]>([]);

    const loadMessages = useCallback(async () => {
        setLoadingMessages(true);
        setMessageError(null);
        try {
            setMessages(await v2Api.inboundMessages(filters));
        } catch (nextError) {
            setMessages(emptyMessages);
            setMessageError(capabilityMessage(asV2Error(nextError), '入站邮件监控'));
        } finally {
            setLoadingMessages(false);
        }
    }, [filters]);

    const loadMonitor = useCallback(
        async (accountId: number) => {
            setLoadingMonitor(true);
            setMonitorError(null);
            try {
                const next = await v2Api.mailboxMonitor(accountId);
                setMonitor(next);
                configForm.setFieldsValue(next);
            } catch (nextError) {
                setMonitor(null);
                setMonitorError(capabilityMessage(asV2Error(nextError), 'Reply-To 邮箱监控'));
            } finally {
                setLoadingMonitor(false);
            }
        },
        [configForm]
    );

    useEffect(() => {
        loadMessages();
    }, [loadMessages]);

    useEffect(() => {
        v2Api
            .mailAccounts({ pageNo: 1, pageSize: 200 })
            .then((result) => setMailAccounts(result.items))
            .catch(() => setMailAccounts([]));
    }, []);

    useEffect(() => {
        const timer = window.setInterval(() => {
            loadMessages();
            if (mailAccountId) loadMonitor(mailAccountId);
        }, 30000);
        return () => window.clearInterval(timer);
    }, [mailAccountId, loadMessages, loadMonitor]);

    const openAccount = async () => {
        const values = await accountForm.validateFields();
        const accountId = Number(values.mailAccountId);
        setMailAccountId(accountId);
        setFilters((current) => ({ ...current, mailAccountId: accountId, pageNo: 1 }));
        loadMonitor(accountId);
    };

    const saveMonitor = async () => {
        if (!mailAccountId) return;
        const values = await configForm.validateFields();
        setSaving(true);
        try {
            const next = await v2Api.updateMailboxMonitor(mailAccountId, {
                ...values,
                mailAccountId,
                pollIntervalSeconds: Number(values.pollIntervalSeconds),
                maxMessagesPerPoll: Number(values.maxMessagesPerPoll),
                initialLookbackDays: Number(values.initialLookbackDays),
                version: monitor?.version,
            });
            setMonitor(next);
            setEditing(false);
            message.success('邮箱监控配置已更新');
        } catch (nextError) {
            setMonitorError(capabilityMessage(asV2Error(nextError), 'Reply-To 邮箱监控'));
        } finally {
            setSaving(false);
        }
    };

    const runMonitor = async () => {
        if (!mailAccountId) return;
        setSaving(true);
        try {
            await v2Api.runMailboxMonitor(mailAccountId);
            message.success('已触发一次增量同步；游标不会被重置');
            window.setTimeout(() => loadMonitor(mailAccountId), 1000);
        } catch (nextError) {
            message.error(capabilityMessage(asV2Error(nextError), '邮箱增量同步'));
        } finally {
            setSaving(false);
        }
    };

    const reconcile = async () => {
        if (!reconciling) return;
        const values = await reconcileForm.validateFields();
        setSaving(true);
        try {
            await v2Api.reconcileInboundMessage(reconciling.id, {
                sendRecipientId: Number(values.sendRecipientId),
                outreachId: Number(values.outreachId),
                version: Number(reconciling.version || 0),
                reason: values.reason,
            });
            message.success('入站邮件已重新关联并追加审计事件');
            setReconciling(null);
            reconcileForm.resetFields();
            loadMessages();
        } catch (nextError) {
            message.error(capabilityMessage(asV2Error(nextError), '入站邮件人工关联'));
        } finally {
            setSaving(false);
        }
    };

    const columns = [
        { title: '接收时间', dataIndex: 'receivedAt', width: 180, render: formatDateTime },
        {
            title: '发件人',
            dataIndex: 'fromMasked',
            width: 190,
            render: (v: string) => v || '已脱敏',
        },
        {
            title: '分类',
            dataIndex: 'classification',
            width: 140,
            render: (v: string) => <StatusTag value={v} />,
        },
        {
            title: '退信原因',
            key: 'bounceReason',
            width: 260,
            render: (_: any, row: InboundMailMessage) =>
                row.classification === 'BOUNCE' ? (
                    <div>
                        {mailFieldLabel(row.bounceCategory, '未识别退信')}
                        {row.bounceStatusCode && (
                            <span className="v2-subline">状态码：{row.bounceStatusCode}</span>
                        )}
                        {row.bounceDiagnostic && (
                            <span className="v2-subline">{row.bounceDiagnostic}</span>
                        )}
                        {row.retryScheduledAt && (
                            <span className="v2-subline">
                                已加入自动重试：{formatDateTime(row.retryScheduledAt)}
                            </span>
                        )}
                    </div>
                ) : '—',
        },
        {
            title: '关联状态',
            dataIndex: 'matchStatus',
            width: 150,
            render: (v: string) => <StatusTag value={v} />,
        },
        {
            title: '匹配依据',
            dataIndex: 'identificationMethod',
            width: 170,
            render: (v: string) => mailFieldLabel(v),
        },
        {
            title: '关联发送',
            key: 'matched',
            width: 170,
            render: (_: any, row: InboundMailMessage) =>
                row.matchedSendRecipientId ? `任务 #${row.matchedSendRecipientId}` : '—',
        },
        {
            title: '错误',
            key: 'lastError',
            render: (_: any, row: InboundMailMessage) =>
                localizedMailError(row.lastErrorMessage, row.lastErrorCode),
        },
        {
            title: '操作',
            key: 'action',
            width: 110,
            fixed: 'right' as 'right',
            render: (_: any, row: InboundMailMessage) => (
                <Button
                    size="small"
                    disabled={!['AMBIGUOUS', 'UNMATCHED'].includes(row.matchStatus || '')}
                    onClick={() => {
                        setReconciling(row);
                        reconcileForm.resetFields();
                    }}
                >
                    人工关联
                </Button>
            ),
        },
    ];

    return (
        <div className="v2-page mail-page">
            <PageHeader
                eyebrow="系统管理 · 批量邮件"
                title="Reply-To 邮箱监控"
            />
            <Card className="v2-filter-card" bordered={false}>
                <Form form={accountForm} layout="inline">
                    <Form.Item name="mailAccountId" label="邮件账号" rules={[{ required: true }]}>
                        <Select
                            showSearch
                            optionFilterProp="children"
                            style={{ width: 320 }}
                            placeholder="请选择邮箱账号"
                        >
                            {mailAccounts.map((account) => (
                                <Select.Option key={account.id} value={account.id}>{`${
                                    account.senderDisplayName || account.providerAccountRef
                                }（${account.replyToAddress}）`}</Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" onClick={openAccount} loading={loadingMonitor}>
                            加载监控配置
                        </Button>
                    </Form.Item>
                </Form>
            </Card>

            {monitorError && (
                <Card className="v2-panel mail-inline-alert" bordered={false}>
                    <Alert type="warning" showIcon message={monitorError} />
                    {mailAccountId && (
                        <Button
                            className="mail-commit-button"
                            type="primary"
                            onClick={() => {
                                configForm.resetFields();
                                configForm.setFieldsValue({
                                    adapterType: 'IMAP',
                                    folderRef: 'INBOX',
                                    imapPort: 993,
                                    imapSecurity: 'SSL_TLS',
                                    status: 'DISABLED',
                                    pollIntervalSeconds: 180,
                                    maxMessagesPerPoll: 200,
                                    initialLookbackDays: 30,
                                });
                                setEditing(true);
                            }}
                        >
                            创建监控配置
                        </Button>
                    )}
                </Card>
            )}
            {monitor && (
                <Card
                    className="v2-panel"
                    bordered={false}
                    title={
                        <Space>
                            <span>{monitor.mailboxRef || `邮件账号 #${mailAccountId}`}</span>
                            <StatusTag value={monitor.status} />
                        </Space>
                    }
                    extra={
                        <Space>
                            <Button
                                icon={<EditOutlined />}
                                onClick={() => {
                                    configForm.setFieldsValue(monitor);
                                    setEditing(true);
                                }}
                            >
                                配置
                            </Button>
                            <Button
                                icon={<CloudSyncOutlined />}
                                loading={saving}
                                onClick={runMonitor}
                            >
                                立即同步
                            </Button>
                        </Space>
                    }
                >
                    <Row gutter={12} className="v2-stat-grid">
                        <Col xs={12} md={6}>
                            <Card className="v2-stat-card">
                                <Statistic
                                    title="轮询间隔"
                                    value={monitor.pollIntervalSeconds || 0}
                                    suffix="秒"
                                />
                            </Card>
                        </Col>
                        <Col xs={12} md={6}>
                            <Card className="v2-stat-card">
                                <Statistic
                                    title="连续失败"
                                    value={monitor.consecutiveFailures || 0}
                                />
                            </Card>
                        </Col>
                        <Col xs={12} md={6}>
                            <Card className="v2-stat-card">
                                <Statistic
                                    title="单次上限"
                                    value={monitor.maxMessagesPerPoll || 0}
                                />
                            </Card>
                        </Col>
                        <Col xs={12} md={6}>
                            <Card className="v2-stat-card">
                                <Statistic
                                    title="初始回溯"
                                    value={monitor.initialLookbackDays || 0}
                                    suffix="天"
                                />
                            </Card>
                        </Col>
                    </Row>
                    <Descriptions bordered size="small" column={2}>
                        <Descriptions.Item label="适配器">
                            {mailFieldLabel(monitor.adapterType, '其他接入方式')}
                        </Descriptions.Item>
                        <Descriptions.Item label="文件夹">
                            {monitor.folderRef || 'INBOX'}
                        </Descriptions.Item>
                        <Descriptions.Item label="最近成功">
                            {formatDateTime(monitor.lastSucceededAt)}
                        </Descriptions.Item>
                        <Descriptions.Item label="下次轮询">
                            {formatDateTime(monitor.nextPollAt)}
                        </Descriptions.Item>
                        <Descriptions.Item label="最后错误" span={2}>
                            {localizedMailError(monitor.lastErrorMessage, monitor.lastErrorCode)}
                        </Descriptions.Item>
                    </Descriptions>
                </Card>
            )}

            <Card
                className="v2-panel v2-section"
                bordered={false}
                title="入站邮件治理队列"
                extra={
                    <Button
                        icon={<ReloadOutlined />}
                        loading={loadingMessages}
                        onClick={loadMessages}
                    >
                        刷新
                    </Button>
                }
            >
                <Space className="mail-table-filter">
                    <span>关联状态</span>
                    <Select
                        style={{ width: 190 }}
                        value={filters.matchStatus}
                        onChange={(matchStatus) =>
                            setFilters({ ...filters, matchStatus, pageNo: 1 })
                        }
                    >
                        {[
                            'RECEIVED',
                            'PROCESSING',
                            'MATCHED',
                            'UNMATCHED',
                            'AMBIGUOUS',
                            'RETRY',
                            'DEAD',
                            'IGNORED',
                        ].map((status) => (
                            <Select.Option key={status} value={status}>
                                {statusLabel(status)}
                            </Select.Option>
                        ))}
                    </Select>
                    <span>消息分类</span>
                    <Select
                        allowClear
                        placeholder="全部分类"
                        style={{ width: 170 }}
                        value={filters.classification}
                        onChange={(classification) =>
                            setFilters({ ...filters, classification, pageNo: 1 })
                        }
                    >
                        {['BOUNCE', 'HUMAN_REPLY', 'AUTO_REPLY', 'OTHER'].map((status) => (
                            <Select.Option key={status} value={status}>
                                {statusLabel(status)}
                            </Select.Option>
                        ))}
                    </Select>
                </Space>
                {messageError ? (
                    <Alert type="warning" showIcon message={messageError} />
                ) : (
                    <Table
                        rowKey="id"
                        loading={loadingMessages}
                        columns={columns}
                        dataSource={messages.items}
                        scroll={{ x: 1410 }}
                        pagination={{
                            current: messages.pageNo,
                            pageSize: messages.pageSize,
                            total: messages.total,
                            showSizeChanger: true,
                            pageSizeOptions: MAIL_PAGE_SIZE_OPTIONS,
                            showTotal: (total) => `共 ${total} 封入站邮件`,
                            onChange: (pageNo, pageSize) => {
                                rememberMailPageSize('inbound-messages', pageSize);
                                setFilters({ ...filters, pageNo, pageSize });
                            },
                        }}
                    />
                )}
            </Card>

            <Modal
                title="配置 Reply-To 邮箱监控"
                visible={editing}
                onOk={saveMonitor}
                confirmLoading={saving}
                onCancel={() => setEditing(false)}
                width={680}
            >
                <Alert
                    type="info"
                    showIcon
                    message="密码或授权码仅在保存时提交，并由服务端加密保存；建议使用只读收件权限并与发件凭据分离。"
                />
                <Form form={configForm} layout="vertical">
                    <Row gutter={12}>
                        <Col span={12}>
                            <Form.Item
                                name="adapterType"
                                label="接入方式"
                                rules={[{ required: true }]}
                            >
                                <Select>
                                    <Select.Option value="PROVIDER_API">
                                        服务商增量 API
                                    </Select.Option>
                                    <Select.Option value="IMAP">IMAP UID</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="status" label="状态" rules={[{ required: true }]}>
                                <Select>
                                    <Select.Option value="ACTIVE">启用</Select.Option>
                                    <Select.Option value="PAUSED">暂停</Select.Option>
                                    <Select.Option value="DISABLED">停用</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item
                        name="mailboxRef"
                        label="Reply-To 邮箱"
                        rules={[{ required: true }, { type: 'email' }]}
                    >
                        <Input maxLength={320} />
                    </Form.Item>
                    <Row gutter={12}>
                        <Col span={12}>
                            <Form.Item
                                name="imapHost"
                                label="IMAP 主机"
                                rules={[{ required: true }]}
                            >
                                <Input placeholder="imap.example.com" />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                name="imapPort"
                                label="端口"
                                initialValue={993}
                                rules={[{ required: true }]}
                            >
                                <InputNumber min={1} max={65535} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                name="imapSecurity"
                                label="加密"
                                initialValue="SSL_TLS"
                                rules={[{ required: true }]}
                            >
                                <Select>
                                    <Select.Option value="SSL_TLS">SSL/TLS</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item name="imapUsername" label="IMAP 用户名" rules={[{ required: true }]}>
                        <Input maxLength={320} />
                    </Form.Item>
                    <Form.Item
                        name="folderRef"
                        label="文件夹"
                        initialValue="INBOX"
                        rules={[{ required: true }]}
                    >
                        <Input maxLength={255} />
                    </Form.Item>
                    <Form.Item
                        name="credentialPassword"
                        label="IMAP 密码/授权码"
                        extra="仅在保存时提交，服务端加密后写入数据库，页面和接口不会回显"
                        rules={[{ required: !monitor || !monitor.credentialConfigured }]}
                    >
                        <Input.Password
                            maxLength={320}
                            autoComplete="new-password"
                            placeholder={monitor ? '留空表示不修改现有密码' : '请输入密码或授权码'}
                        />
                    </Form.Item>
                    <Row gutter={12}>
                        <Col span={8}>
                            <Form.Item
                                name="pollIntervalSeconds"
                                label="轮询间隔（秒）"
                                rules={[{ required: true }]}
                            >
                                <InputNumber min={30} max={86400} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="maxMessagesPerPoll"
                                label="单次消息上限"
                                rules={[{ required: true }]}
                            >
                                <InputNumber min={1} max={5000} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="initialLookbackDays"
                                label="首次回溯天数"
                                rules={[{ required: true }]}
                            >
                                <InputNumber min={1} max={365} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>

            <Modal
                title="人工关联入站回复"
                visible={!!reconciling}
                onOk={reconcile}
                confirmLoading={saving}
                onCancel={() => setReconciling(null)}
            >
                <Alert
                    type="warning"
                    showIcon
                    message="提交前请核对发件人、RFC 引用和时间窗证据。人工关联会追加纠错事件，不会覆盖原始入站事实。"
                />
                <Form form={reconcileForm} layout="vertical">
                    <Form.Item
                        name="sendRecipientId"
                        label="发送任务 ID"
                        rules={[{ required: true }]}
                    >
                        <InputNumber min={1} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="outreachId" label="触达记录 ID" rules={[{ required: true }]}>
                        <InputNumber min={1} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                        name="reason"
                        label="关联依据"
                        rules={[{ required: true, whitespace: true }]}
                    >
                        <Input.TextArea rows={3} />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default InboundMonitorPage;
